/**
 * P-3 施策台帳一覧 (`/board-initiatives`)。設計: docs/design/v05-board-meeting-pdca-detail.md §7-3。
 *
 * ⚠️ `GET /nodes` は使わない (§8-182 と同型)。グループ見出し・作成フォームの組織選択肢は
 * それぞれ「API が返した施策の node_id」「`GET /board-initiatives/creatable-nodes`」から構築する。
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BoardEmptyState } from '@/components/board/BoardEmptyState';
import { InitiativeOwnersEditor, type InitiativeOwner } from '@/components/board/InitiativeOwnersEditor';
import type { BoardNode } from '@/lib/board-node';

type BoardInitiativeOwner = { employee_id: string; name: string | null; role_label: string | null };
type BoardInitiative = {
  initiative_id: string; node_id: string; title: string; description: string | null;
  origin_meeting_id: string | null; origin_finding_id: string | null;
  owners: BoardInitiativeOwner[];
  due_date: string | null; status: 'draft' | 'active' | 'done' | 'cancelled' | 'carried_over';
  target_kpi_type: string | null; created_at: string;
};

const STATUS_LABEL: Record<BoardInitiative['status'], string> = {
  draft: '下書き', active: '進行中', done: '達成', cancelled: '取消', carried_over: '継続中',
};
const STATUS_CLASS: Record<BoardInitiative['status'], string> = {
  draft: 'bg-surface-sunken text-text-sub border-surface-border',
  active: 'bg-group-board-bg text-group-board border-group-board/20',
  done: 'bg-success-bg text-success-text border-success-border',
  cancelled: 'bg-surface-sunken text-text-sub border-surface-border',
  carried_over: 'bg-alert-bg text-alert-text border-alert-border',
};
const todayLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Draft = { title: string; nodeId: string; owners: InitiativeOwner[]; dueDate: string; description: string };

export function BoardInitiativesListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [nodeFilter, setNodeFilter] = useState('__all__');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: initiativesData, isLoading, isError } = useQuery<{ initiatives: BoardInitiative[] }>({
    queryKey: ['board-initiatives'],
    queryFn: () => apiFetch('/board-initiatives'),
  });
  const initiatives = initiativesData?.initiatives ?? [];

  const { data: creatableData } = useQuery<{ nodes: BoardNode[] }>({
    queryKey: ['board-initiatives', 'creatable-nodes'],
    queryFn: () => apiFetch('/board-initiatives/creatable-nodes'),
  });
  const creatableNodes = creatableData?.nodes ?? [];
  const writableNodeIds = useMemo(() => new Set(creatableNodes.map((n) => n.node_id)), [creatableNodes]);
  const creatableNodeById = useMemo(() => new Map(creatableNodes.map((n) => [n.node_id, n])), [creatableNodes]);

  // ⚠️ §7-3: グループ見出しは API が返した施策の node_id から構築する (GET /nodes は使わない)
  const nodeIdsInList = useMemo(() => [...new Set(initiatives.map((i) => i.node_id))], [initiatives]);
  const nodeLabel = (nodeId: string) => creatableNodeById.get(nodeId)?.name ?? nodeId;

  const filtered = useMemo(() => initiatives.filter((i) => {
    if (statusFilter !== '__all__' && i.status !== statusFilter) return false;
    if (nodeFilter !== '__all__' && i.node_id !== nodeFilter) return false;
    return true;
  }), [initiatives, statusFilter, nodeFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, BoardInitiative[]>();
    for (const i of filtered) {
      if (!map.has(i.node_id)) map.set(i.node_id, []);
      map.get(i.node_id)!.push(i);
    }
    return map;
  }, [filtered]);

  const createMut = useMutation({
    mutationFn: (d: Draft) => apiFetch<{ initiative_id: string }>('/board-initiatives', {
      method: 'POST',
      body: JSON.stringify({
        node_id: d.nodeId, title: d.title, description: d.description || null,
        owners: d.owners, due_date: d.dueDate || null,
      }),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['board-initiatives'] });
      setDraft(null);
      toast.success('施策を登録しました');
      navigate(`/board-initiatives/${res.initiative_id}`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '施策の登録に失敗しました'),
  });

  const canCreate = creatableNodes.length > 0;
  const openCreateModal = () => setDraft({ title: '', nodeId: creatableNodes[0]?.node_id ?? '', owners: [], dueDate: '', description: '' });
  const canSaveDraft = !!draft && draft.title.trim() !== '' && draft.nodeId !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-text-main">施策台帳</h1>
          <p className="text-sm text-text-sub mt-1">会議で決めた打ち手の進捗と効果測定を管理します。</p>
        </div>
        {canCreate && <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openCreateModal}>施策を登録</Button>}
      </div>

      {/* §7-3: 375px以下はフィルタをアコーディオンに畳む */}
      <div className="border border-surface-border rounded-lg bg-white">
        <button type="button" className="sm:hidden w-full min-h-[44px] px-3 text-left text-sm text-text-body flex items-center justify-between"
          onClick={() => setFiltersOpen((v) => !v)}>
          <span>絞り込み</span><span className="text-text-sub">{filtersOpen ? '閉じる' : '開く'}</span>
        </button>
        <div className={`${filtersOpen ? 'flex' : 'hidden'} sm:flex flex-wrap gap-2 p-3`}>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 min-h-[44px]" aria-label="状態で絞り込み"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">すべての状態</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {nodeIdsInList.length > 1 && (
            <Select value={nodeFilter} onValueChange={setNodeFilter}>
              <SelectTrigger className="w-full sm:w-56 min-h-[44px]" aria-label="対象組織で絞り込み"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">すべての組織</SelectItem>
                {nodeIdsInList.map((id) => <SelectItem key={id} value={id}>{nodeLabel(id)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込んでいます…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">施策一覧の取得に失敗しました。</p>
      ) : filtered.length === 0 ? (
        <BoardEmptyState
          title={initiatives.length === 0 ? '施策がまだ登録されていません' : '絞り込み条件に一致する施策がありません'}
          action={canCreate && initiatives.length === 0 ? { label: '施策を登録', onClick: openCreateModal } : undefined}
        />
      ) : (
        <div className="space-y-5">
          {[...grouped.entries()].map(([nodeId, items]) => (
            <div key={nodeId} className="space-y-2">
              <h2 className="text-sm font-semibold text-text-body flex items-center gap-1.5">
                {nodeLabel(nodeId)}
                {!writableNodeIds.has(nodeId) && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub whitespace-nowrap">閲覧のみ</span>
                )}
              </h2>
              <ul className="space-y-2">
                {items.map((i) => {
                  const overdue = i.status === 'active' && i.due_date != null && i.due_date < todayLocalDateString();
                  return (
                    <li key={i.initiative_id}>
                      <button type="button" onClick={() => navigate(`/board-initiatives/${i.initiative_id}`)}
                        className="w-full text-left border border-surface-border rounded-lg bg-white p-3 min-h-[44px] hover:bg-surface-sunken">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-sm text-text-body break-words line-clamp-2">{i.title}</span>
                          <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${overdue ? 'bg-alert-bg text-alert-text border-alert-border' : STATUS_CLASS[i.status]}`}>
                            {overdue ? '期限超過' : STATUS_LABEL[i.status]}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1 text-xs text-text-sub">
                          {i.owners.slice(0, 2).map((o) => (
                            <span key={o.employee_id}
                              className="px-1.5 py-0.5 rounded border border-surface-border bg-white text-text-body whitespace-nowrap">
                              {o.name ?? '担当者'}
                            </span>
                          ))}
                          {i.owners.length > 2 && (
                            <span
                              className="px-1.5 py-0.5 rounded border border-surface-border bg-white text-text-sub whitespace-nowrap"
                              title={i.owners.slice(2).map((o) => o.name ?? '担当者').join('、')}>
                              +{i.owners.length - 2}
                            </span>
                          )}
                          {i.due_date && <span className="tabular-nums">期限: {i.due_date}</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!draft} title="施策を登録" onClose={() => setDraft(null)} size="md"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveDraft || createMut.isPending} loading={createMut.isPending}
              onClick={() => draft && createMut.mutate(draft)}>登録</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bi-title">タイトル</label>
              <input id="bi-title" type="text" value={draft.title} maxLength={200}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bi-node">対象組織</label>
              <Select value={draft.nodeId} onValueChange={(v) => setDraft({ ...draft, nodeId: v })}>
                <SelectTrigger id="bi-node" aria-label="対象組織"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {creatableNodes.map((n) => (
                    <SelectItem key={n.node_id} value={n.node_id}>{'　'.repeat(n.depth_level)}{n.name} ({n.node_type_label})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">担当者 (任意、複数可)</label>
              <InitiativeOwnersEditor value={draft.owners} onChange={(owners) => setDraft({ ...draft, owners })} enabled={!!draft} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bi-due">期限 (任意)</label>
              <input id="bi-due" type="date" value={draft.dueDate} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bi-desc">説明 (任意)</label>
              <textarea id="bi-desc" value={draft.description} maxLength={2000} rows={3}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className="w-full px-3 py-2 rounded border border-surface-border text-sm" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
